import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import get from 'lodash/get';
import Seo from '../components/seo';
import Layout from '../components/layout';
import SocialShare from '../components/SocialShare';
import HighlightedPopover from '../components/HighlightedPopover';
import { Breadcrumbs, Divider, Stack } from '@mui/material';
import ContentVisualizer from '../components/ContentVisualizer';
import getAnonyUserId from '../services/getAnonyUserId';
import { reportData } from '../services/dataReport';
import * as styles from './LessonPage.module.css';
import ShowModalTrigger from '../components/ShowModalTrigger';
import LessonBreadcrumbs from '../components/LessonBreadcrumbs';
import { Button } from '@mui/material';
import MacmaRobotIcon from '../assets/macma-robot.svg';
import { Activities } from '../components/Activities';
import useWindowDimensions from '../hooks/useWindowDimensions';
import LessonCard from '../components/LessonCard';
import reportUserBrowser from '../utils/reportUserBrowser';
import CreateAccountWall from '../components/CreateAccountWall';

const LessonPage = (props) => {
  const screen = useWindowDimensions();
  const isMobile = screen.width < 810;

  const [open, setOpen] = useState(false);

  const lesson = get(props, 'data.contentfulAula');
  const site = get(props, 'data.site');
  const contentfulId = props.pageContext.id;
  const { bnccSkills, closedExerciseResponses, exerciseSkill } = lesson;

  const userId = getAnonyUserId();
  const firstImage = lesson?.exerciseMedia?.find(
    (exercise) => exercise.slice(-4) === '.png'
  );
  const firstVideo = lesson?.videoContents?.[0];

  const level = lesson?.knowledgeArea ? 'Ensino Médio' : 'Fund. 2';

  const subject = lesson.subject?.name || lesson.knowledgeArea?.name;
  const filterSubject = lesson?.subject
    ? `{"subject":${lesson?.subject?.number}}`
    : `{"knowledgeArea":${lesson?.knowledgeArea?.number}}`;

  const language = lesson?.workedLanguage === 'Inglês' ? 'Aula em inglês' : '';

  const competenceNumber =
    lesson?.competence?.childMarkdownRemark?.html?.split('"')[1];
  const competenceText = lesson?.competence?.childMarkdownRemark?.html
    ?.split('<li>')[1]
    ?.split('</li>')[0];

  const bncc = lesson?.bnccSkills?.[0];

  const isBrowser = typeof window !== 'undefined';
  const passingSearch = isBrowser
    ? window.location.search.replace('?', '&')
    : '';

  const getSkipPhone = () => {
    return isBrowser && !!localStorage.getItem('phone_number')
      ? '&skipPhone=true'
      : '';
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      reportData(
        userId,
        'selectedClassSite',
        contentfulId + window.location.search
      );

      reportUserBrowser(userId);
    }
  }, [userId, contentfulId]);

  const onShare = (sharedContent) => {
    reportData(userId, 'status', 'share_' + sharedContent);
  };

  const handleTrigger = () => {
    setOpen(true);
    reportData(userId, 'status', 'bloqueio_seo?source=scroll');
  };

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  }, []);

  return (
    <>
      <Seo
        title={lesson.title}
        description={lesson.intro.childMarkdownRemark.excerpt}
        image={firstImage}
        video={firstVideo}
        createdAt={lesson.createdAt}
        updatedAt={lesson.updatedAt}
        urlSlug={lesson.urlSlug}
        bncc={bnccSkills?.[0]}
        subject={lesson.subject?.name || lesson.knowledgeArea?.name}
        keywords={lesson.keywords}
      />
      <ShowModalTrigger onTrigger={handleTrigger}>
        <Layout
          location={props.location}
          footerProps={{
            contentfulId,
            userId,
            lesson,
          }}
        >
          {isMobile && (
            <section>
              <div className={styles.newFeatContainerMobile}>
                <div className={styles.descriptionAIContainer}>
                  <img className={styles.iconAI} src={MacmaRobotIcon} alt="" />
                  <p className={styles.descriptionAIMobile}>
                    Experimente novos recursos para a aula com inteligência
                    artificial (IA)!
                  </p>
                </div>

                <Button
                  className={styles.buttonAIMobile}
                  variant="contained"
                  href={`${
                    process.env.GATSBY_APP_BASE_URL
                  }/aula/${contentfulId}?s=gerarConteudoIA_SEO&utm_origin=SEO&SEOID=${userId}${getSkipPhone()}${passingSearch}`}
                >
                  Testar IA
                </Button>
              </div>
            </section>
          )}

          <div className="container-layout">
            <div className={styles.flexWrapper}>
              <Stack spacing={2}>
                <Breadcrumbs aria-label="breadcrumb">
                  <LessonBreadcrumbs
                    text={level}
                    queryParams={`etiquetaFiltro={"level":"${level}"}`}
                    userId={userId}
                    skipPhone={getSkipPhone()}
                    passingSearch={passingSearch}
                  />
                  <LessonBreadcrumbs
                    text={subject}
                    queryParams={`etiquetaFiltro=${filterSubject}`}
                    userId={userId}
                    skipPhone={getSkipPhone()}
                    passingSearch={passingSearch}
                  />

                  <LessonBreadcrumbs
                    text={lesson?.unit?.name}
                    url={`/unidade/${lesson?.unit?.contentful_id}`}
                    queryParams={`aulaOrigem=${contentfulId}`}
                    userId={userId}
                    skipPhone={getSkipPhone()}
                    passingSearch={passingSearch}
                  />

                  {lesson?.workedLanguage === 'Inglês' && (
                    <LessonBreadcrumbs
                      text="Aula em inglês"
                      queryParams={`etiquetaFiltro=${language}`}
                      userId={userId}
                      skipPhone={getSkipPhone()}
                      passingSearch={passingSearch}
                    />
                  )}
                </Breadcrumbs>
              </Stack>
            </div>

            <h1 className={styles.titleH1Intro} id="introducao">
              {lesson.title}
            </h1>

            <section className={styles.sectionPopover}>
              <HighlightedPopover
                prefix={
                  lesson?.competence
                    ? `Competência ${competenceNumber}`
                    : 'BNCC'
                }
                shortText={competenceText || bncc?.code}
                onOpenText={bncc?.description?.childMarkdownRemark?.html}
              />
              {exerciseSkill?.verb && !lesson?.competence && (
                <HighlightedPopover
                  prefix={`Nível ${
                    { 1: 'Básico', 2: 'Intermediário', 3: 'Avançado' }[
                      lesson?.learningLevel
                    ] || ''
                  }`}
                  shortText={exerciseSkill[0]?.verb}
                  onOpenText={exerciseSkill[0]?.description}
                />
              )}
            </section>

            <div
              dangerouslySetInnerHTML={{
                __html: lesson.intro?.childMarkdownRemark?.html,
              }}
            />

            <section>
              <div style={{ marginBottom: 20 }}>
                {lesson.content?.map((content, index) => {
                  return (
                    <ContentVisualizer
                      contentfulId={contentfulId}
                      userId={userId}
                      key={'content-index-' + index}
                      asset={content}
                      lesson={lesson}
                    />
                  );
                })}
                {lesson.videoContents?.map((content, index) => {
                  return (
                    <React.Fragment key={'videoContents-index-' + index}>
                      <h2 className={styles.titleH2Content} id="material-apoio">
                        Material de apoio
                      </h2>
                      <p className={styles.subtitleContent}>
                        Você pode assistir o vídeo para complementar a aula:
                      </p>
                      <ContentVisualizer
                        contentfulId={contentfulId}
                        userId={userId}
                        asset={content}
                        lesson={lesson}
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            </section>

            <Divider className="divider" />

            <Activities
              lesson={lesson}
              closedExerciseResponses={closedExerciseResponses}
              contentfulId={contentfulId}
              userId={userId}
              isMobile={isMobile}
            />

            {!isMobile && (
              <section>
                <div className={styles.ondemandWrapper}>
                  <div className={styles.newFeatContainer}>
                    <img src={MacmaRobotIcon} alt="" />
                    <h2 className={styles.newFeatTitle}>
                      Experimente novos recursos para a aula!
                    </h2>
                  </div>
                  <p className={styles.newFeatDescription}>
                    Quer mais formas de complementar a aula? Experimente as
                    novas funções do <b>AprendiZAP</b> que utilizam{' '}
                    <b>inteligência artificial (IA)</b> para criar mais
                    conteúdos para você!
                  </p>

                  <div className={styles.btnAIContainer}>
                    <Button
                      variant="outlined"
                      className={styles.buttonAI}
                      href={`${
                        process.env.GATSBY_APP_BASE_URL
                      }/aula/${contentfulId}?s=gerarConteudoIA_SEO&utm_origin=SEO&SEOID=${userId}${getSkipPhone()}${passingSearch}`}
                    >
                      Testar inteligência artificial
                    </Button>
                  </div>
                </div>
                <Divider className="divider" />
              </section>
            )}

            <section id="aulas-relacionadas">
              <h2 className={styles.titleH2Content}>⭐ Aulas relacionadas</h2>
              <div className={styles.relatedLessonsWrapper}>
                {lesson &&
                  lesson.relatedLessons.map((relatedLesson, index) => (
                    <div
                      className={styles.relatedLessonItemContainer}
                      key={
                        'related-lesson-key' -
                        relatedLesson.contentful_id +
                        '-idx-' +
                        index
                      }
                    >
                      <LessonCard lesson={relatedLesson} />
                    </div>
                  ))}
              </div>
            </section>

            <SocialShare
              onShare={onShare}
              lessonTitle={lesson?.title}
              url={`${site?.siteMetadata?.siteUrl}/${lesson?.urlSlug}/`}
            />
          </div>
        </Layout>
      </ShowModalTrigger>

      <CreateAccountWall
        originAction="scroll"
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

export default LessonPage;

export const pageQuery = graphql`
  query LessonById($id: String!) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    contentfulAula(contentful_id: { eq: $id }) {
      urlSlug
      title
      year
      createdAt
      updatedAt
      keywords
      relatedLessons {
        contentful_id
        title
        intro {
          childMarkdownRemark {
            html
          }
        }
      }
      subject {
        name
        number
      }
      knowledgeArea {
        name
        number
      }
      closedExercise {
        childMarkdownRemark {
          html
        }
      }
      closedExerciseImages
      closedExerciseOptions {
        childMarkdownRemark {
          html
        }
      }
      closedExerciseResponses {
        correct
        incorrect
        partiallyCorrect
        feedback {
          A
          B
          C
        }
      }
      content
      videoContents
      exerciseMedia
      exerciseText {
        childMarkdownRemark {
          html
        }
      }
      keywords
      correctionMedia
      correctionText {
        childMarkdownRemark {
          html
        }
      }
      bnccSkills {
        code
        description {
          childMarkdownRemark {
            html
          }
        }
      }
      intro {
        childMarkdownRemark {
          html
          excerpt
        }
      }
      workedLanguage
      learningLevel
      exerciseSkill {
        verb
        description
      }
      competence {
        childMarkdownRemark {
          html
        }
      }
      childrenContentfulAulaActiveMethodologiesJsonNode {
        name
        activities {
          contextualization
          supportMaterials
        }
      }
      unit {
        contentful_id
        name
      }
    }
  }
`;
